import { useCallback, useEffect, useState } from 'react';
import { applyThroughAgentText, CardWrapper, formDescription, formFooter, helpText, conflictError, hdrText } from '@/components/Common';
import { RegisterSchema } from '@/schema';
import { FieldValues, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@curtin-dxp/curtin-ui';
import { Form } from '@/components/ui/form';
import { InputField } from './InputField';
import { SelectField } from './SelectField';
import { DatePickerField } from './DatePickerField';
import { AccountRegistered } from './AccountRegistered';
import { ReloadIcon } from '@radix-ui/react-icons';
import { trpc } from '@/data-loaders';
import { StudentPayLoad } from '#server/models';
import { CaptchaDialog } from './CaptchaDialog';
import { RadioGroupField } from './RadioGroupField';
import { setHighRiskCountryCodes, isCountryHighRisk } from '@/lib/validation-helper';
import { isCountryCodeIncludedForCitizenship } from '@/lib/country-filter-helper';
import { useSearchParams } from 'react-router-dom';
import { config } from '@/env-exports';
import { isValidUrl } from '@/lib/url-validator';
import { ADOBE_MC_PARAM, appendAdobeMc } from '@/lib/adobe-mc';
import { isWafCaptchaChallenge } from '@/lib/waf-captcha';

export const RegistrationForm = () => {
  const [serverError, setServerError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAccountRegistered, setIsAccountRegistered] = useState<boolean>(false);
  const [showCaptchaDialog, setShowCaptchaDialog] = useState<boolean>(false);
  const [waitingOnCountryUpdate, setWaitingOnCountryUpdate] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<StudentPayLoad>({
    firstName: '',
    lastName: '',
    email: '',
    gender: '',
    dob: '',
    citizenship: '',
    countryOfCitizenship: '',
  });
  const studentCreator = trpc.registerStudent.useMutation();
  const { data: countryCodesData, error: countryCodesError } = trpc.countryCodes.useQuery(undefined, {
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Retry only if the error is not an authentication error or a bad request error
      return error.data?.code === 'UNAUTHORIZED' || error.data?.code === 'BAD_REQUEST' ? false : failureCount < 3;
    }
  });
  const countryOfCitizenshipOptionsFull = countryCodesData ? countryCodesData.countries : [];
  setHighRiskCountryCodes(countryCodesData?.highRiskCountryCodes);

  const form = useForm<FieldValues>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      gender: '',
      dob: null,
      citizenship: '',
      countryOfCitizenship: '',
      isHdr: '',
      isAustralianVisa: '',
    },
  });
  const {
    handleSubmit,
    formState: { isValid },
    setError,
    setValue,
    trigger,
    watch
  } = form;

  const citizenship = watch('citizenship');
  const countryOfCitizenship = watch('countryOfCitizenship');
  const isHdr = watch('isHdr');
  const isAustralianVisa = watch('isAustralianVisa');

  const genderOptions: [value: string, optionLabel: string][] = [
    ['M', 'Male/Man'],
    ['F', 'Female/Woman'],
    ['N', 'Non-Binary'],
    ['D', 'Different Term'],
    ['P', 'Prefer not to answer']
  ];

  const citizenOptions: [value: string, optionLabel: string][] = [
    ['1', 'Australian Citizen'],
    ['2', 'New Zealand Citizen'],
    ['3', 'Permanent Resident of Australia'],
    ['4', 'International Student applying to study in Australia'],
    ['5', 'International Student applying to a campus outside Australia or Fully Online'],
    ['8', 'Australian Permanent Humanitarian Visa Holder'],
    ['P', 'Pacific Engagement Visa Holder']
  ];

  const yesNoOptions = {
    true: 'Yes',
    false: 'No'
  }

  const internationalStudentCitizenshipCodes = ['4', '5'];

  // Initialise variables controlling the display of additional fields
  const showHdr = isCountryHighRisk(citizenship, countryOfCitizenship);
  const showAustralianVisa = showHdr && isHdr === 'false';
  const aYearFromNow = new Date();
  aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1);
  const isHighRiskApplicant = showAustralianVisa && isAustralianVisa === 'false';

  // Populate the country of citizenship options.  If the selected citizenship has a country filter then apply the filter to the full list
  // Otherwise set the country of citizenship options to the full list
  let countryOfCitizenshipOptions: [value: string, optionLabel: string][];
  if (citizenship) {
    countryOfCitizenshipOptions = countryOfCitizenshipOptionsFull.filter(([code]) => isCountryCodeIncludedForCitizenship(code, citizenship));

    // If we're filtering the country of citizenship options to one value then set country of citizenship to that value.
    // Needs to be set on a timer since the options might not have been updated to include the selected citizenship yet.
    // There's a window of opportunity between selecting the citizenship and the timeout setting the country value where
    // you could submit the form with an invalid country of citizenship.  Setting waitingOnCountryUpdate during this period
    // prevents this from happening.
    if (countryOfCitizenshipOptions.length === 1 && countryOfCitizenship !== countryOfCitizenshipOptions[0][0]) {
      if (!waitingOnCountryUpdate) {
        setWaitingOnCountryUpdate(true);
        setTimeout(() => {
          setWaitingOnCountryUpdate(false);
          setValue('countryOfCitizenship', countryOfCitizenshipOptions[0][0]);
          trigger('countryOfCitizenship'); // Trigger validation to update the form state
        }, 200);
      }
      // If the country of citizenship is not included in the options, clear the country of citizenship field
    } else if (countryOfCitizenship && !isCountryCodeIncludedForCitizenship(countryOfCitizenship, citizenship)) {
      if (!waitingOnCountryUpdate) {
        setWaitingOnCountryUpdate(true);
        setTimeout(() => {
          setWaitingOnCountryUpdate(false);
          setValue('countryOfCitizenship', ''); // No validation trigger needed in this case
        }, 200);
      }
    }
  } else {
    countryOfCitizenshipOptions = countryOfCitizenshipOptionsFull;
  }

  const onSubmit = (data: FieldValues) => {
    if (isValid && !waitingOnCountryUpdate) {
      if (isHighRiskApplicant) {
        window.location.href = config.findAgentUrl!;
      } else {
        setIsLoading(true);
        const payload = { ...data } as StudentPayLoad;
        const returnUrl = searchParams.get('returnUrl');

        if (returnUrl && isValidUrl(returnUrl, config.redirectDomainWhitelist)) {
          payload.returnUrl = returnUrl;
        } else if (config.studylinkEnabled &&
          !returnUrl &&
          internationalStudentCitizenshipCodes.includes(payload.citizenship)) {
          // International students (with no other returnUrl set) should be returned to studylink after registration
          payload.returnUrl = config.studylinkRedirectUrl ?? '';
        }
        if (payload.returnUrl) {
          // Carry the Adobe CJA parameter through so the user's journey stays visible after registration
          payload.returnUrl = appendAdobeMc(payload.returnUrl, searchParams.get(ADOBE_MC_PARAM));
        }
        setData(payload);
        // Submit straight away, the CAPTCHA puzzle is only shown if WAF challenges the request
        studentCreator.mutate(payload);
      }
    }
  };

  const handleOnKeyDown = () => {
    if (!studentCreator.isLoading) studentCreator.reset();
  };
  const handleOnSolvingPuzzleSuccess = useCallback(() => {
    studentCreator.mutate(data as StudentPayLoad);
    setShowCaptchaDialog(false);
  }, [data, studentCreator]);

  useEffect(() => {
    if (studentCreator.error && !studentCreator.isLoading) {
      if (isWafCaptchaChallenge(studentCreator.error)) {
        // Keep loading while the user solves the puzzle, the registration is retried on success
        setShowCaptchaDialog(true);
        return;
      }
      setIsLoading(false);
      switch (studentCreator.error.data?.code) {
        case 'CONFLICT':
          setError('email', { message: 'This email address is already in use' });
          break;
        default:
          setServerError(true);
          break;
      }
    } else if (studentCreator.data !== undefined && studentCreator.data) {
      setIsAccountRegistered(true);
      setIsLoading(false);
    }
  }, [setError, studentCreator.error, studentCreator.data, studentCreator.isLoading]);

  return (
    <>
      {showCaptchaDialog && isValid && <CaptchaDialog onCaptchaSuccess={handleOnSolvingPuzzleSuccess} />}
      <main className="w-full">
        <div
          className={`${isAccountRegistered ? 'h-screen' : 'md:mt-10'} flex flex-col items-center justify-center mt-0 text-base`}
        >
          {!isAccountRegistered && (
            <CardWrapper title="Create an account ">
              <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="flex flex-row gap-x-4">
                    <div className="w-1/2">
                      <InputField name="firstName" label="First name" control={form.control} />
                    </div>
                    <div className="w-1/2">
                      <InputField name="lastName" label="Last name" control={form.control} />
                    </div>
                  </div>
                  <InputField name="email" label="Email address" control={form.control} onKeyDown={handleOnKeyDown} />
                  <SelectField name="gender" label="Gender" control={form.control} options={genderOptions} />
                  <DatePickerField name="dob" label="Date of birth" control={form.control} disabled={(date) => date > new Date() || date < new Date('1900-01-01')} />
                  <SelectField name="citizenship" label="Citizenship or residency status" control={form.control} options={citizenOptions} />
                  <SelectField name="countryOfCitizenship" label="Country of citizenship" control={form.control} options={countryOfCitizenshipOptions} />
                  {showHdr && (<RadioGroupField name="isHdr" label={hdrText} control={form.control} options={yesNoOptions} />)}
                  {showAustralianVisa && (<RadioGroupField name="isAustralianVisa" label="Do you have a valid Australian visa?" control={form.control} options={yesNoOptions} />)}
                  {studentCreator.error?.data?.code === 'CONFLICT' && !isValid ? (
                    <div className="p-4 rounded-md font-medium bg-[#FFD9D9] text-center">
                      {conflictError}
                    </div>
                  ) : null}
                  {serverError || countryCodesError ? (
                    <div className="p-4 rounded-md font-medium bg-[#FFD9D9] text-center">
                      Service unavailable. Please try again later.
                    </div>
                  ) : null}
                  {/* Show "Create account" button unless applicant is high risk, in which case we show "Find an agent" button */
                    isValid && isHighRiskApplicant ? (
                      <>
                        <div className="py-2">{applyThroughAgentText}</div>
                        <Button type="submit" variant="filled-primary" className="w-full">
                          Find an agent
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="py-2">{formDescription}</div>
                        <Button type="submit" variant="filled-primary" className="w-full" disabled={isLoading}>
                          {isLoading && <ReloadIcon className="mr-2 h-5 w-5 animate-spin" />}
                          Create account
                        </Button>
                      </>
                    )
                  }

                </form>
              </Form>
              <div>{formFooter}</div>
            </CardWrapper>
          )}
          {isAccountRegistered && <AccountRegistered />}
          {helpText}
        </div>
      </main>
    </>
  );
};
